import { defineStore } from "pinia";
import { useCleverRouteImportStore } from "./cleverRouteImportStore";
// Der Stauaufschlag wird hier gelesen und nicht von den Aufrufern
// durchgereicht: so beruecksichtigt jeder Metrik-Abruf den Schalter
// automatisch, und Karte und Tabellen koennen nicht auseinanderlaufen.
import { usePlzSurchargeStore } from "./plzSurchargeStore";
import { JmApi } from "../jm-api/jm-api";
import { Adress } from "../jm-data-instances/adress";
import { MetricsManager } from "../data-managers/metrics-manager";
import { RuleContainer } from "../data-managers/rule";
import { Adress as AdressData, Office } from "../model/jmApiResponses";
// import { ZipCodeData } from "../model/jmApiResponses";
import { ZipCodes, ZipCodeData } from "../jm-data-instances/zip-codes";
import { DEFAULT_BORDERS } from "../config/defaultMapInput";
import { MetricBorders, RelativeMetricBorders } from "../model/metrics";
import { Metric } from "../model/metrics";
import { PARAMETERS_BY_TRANSPORT_URL } from "@/config/urls";
import { useOfficeStore } from "./officeStore";
import {
  BestAlternativeFilters,
  DEFAULT_BEST_ALTERNATIVE_FILTERS,
} from "@/model/bestAlternativeModels";
import { TransportService } from "@/services/transport-service";

/**
 * Used to store data, which is relevant for map generation.
 *
 * Devnotes: Add validation for setters.
 * Also you will need to tell the Store to update content, that is related to other inputs.
 * This is mainly done, to safe performance.
 * Later we can implement a commit-function, that will notice, what changed and make the necessary changes itself.
 */
export const useMapInputStore = defineStore("mapInput", {
  state: () => ({
    firstTransport: null as string | null,
    secondTransport: null as string | null,
    metric: null as Metric | null,
    relativeData: null as object | null,
    absoluteClassBorders: DEFAULT_BORDERS.absolute as MetricBorders,
    differenceClassBorders: DEFAULT_BORDERS.difference as MetricBorders,
    relativeClassBorders: DEFAULT_BORDERS.relative as RelativeMetricBorders,
    adresses: null as AdressData[] | null,
    // metrics for first and second transport
    metricsFirst: null as object | null,
    metricsSecond: null as object | null,
    // will be provided externaly
    office: null as Office | null,
    rules: null as RuleContainer | null,
    zipCodeData: null as ZipCodeData[] | null,
    // Best Alternative specific state
    bestAlternativeFilters: {
      ...DEFAULT_BEST_ALTERNATIVE_FILTERS,
    } as BestAlternativeFilters,
  }),
  getters: {
    getFirstTransport(state) {
      if (!state.firstTransport) {
        console.warn("First Transport accessed but no data returned");
      }
      return state.firstTransport;
    },
    getSecondTransport(state) {
      if (!state.secondTransport) {
        console.warn("Second Transport accessed but no data returned");
      }
      return state.secondTransport;
    },
    getMetric(state): null | Metric {
      if (!state.metric) {
        console.warn("Metrics accessed but no data returned");
      }
      return state.metric;
    },
    getAbsoluteClassBorders(state): MetricBorders {
      return state.absoluteClassBorders;
    },
    getDifferenceClassBorders(state): MetricBorders {

      return state.differenceClassBorders;
    },
    getRelativeClassBorders(state): RelativeMetricBorders {
      return state.relativeClassBorders;
    },
    getRelativeData(state) {
      if (!state.relativeData) {
        console.warn("No relative data provided by mapDataStore!");
      }
      return state.relativeData;
    },
    getAdresses(state) {
      if (!state.adresses || state.adresses.length === 0) {
        console.warn("No adresses provided by mapDataStore!");
      }
      return state.adresses;
    },
    getZipCodeData(state) {
      if (!state.zipCodeData || state.zipCodeData.length === 0) {
        console.warn("No zip code data provided by mapDataStore!");
      }
      return state.zipCodeData;
    },
    /**
     * Get Metrics from the first selected transport.
     * @param state state of application
     * @returns object
     */
    getMetricsFirst(state) {
      if (!state.metricsFirst) {
        console.warn("No metrics provided by mapDataStore!");
      }
      return state.metricsFirst;
    },
    /**
     * Get Metrics from the second application.
     * @param state state of application
     * @returns object
     */
    getMetricsSecond(state) {
      if (!state.metricsSecond) {
        console.warn("No metrics provided by mapDataStore!");
      }
      return state.metricsSecond;
    },
    getOffice(state) {
      if (!state.office) {
        console.warn("No office set in mapDataStore!");
      }
      return state.office;
    },
    /**
     * Get current best alternative filter settings
     */
    getBestAlternativeFilters(): BestAlternativeFilters {
      return this.bestAlternativeFilters;
    },
  },
  actions: {
    setFirstTransport(val: string) {
      this.firstTransport = val;
    },
    setSecondTransport(val: string) {
      this.secondTransport = val;
    },
    setMetric(val: Metric) {
      this.metric = val;
    },
    setAbsoluteClassBorders(borders: MetricBorders) {
      this.absoluteClassBorders = borders;
    },
    setDifferenceClassBorders(borders: MetricBorders) {
      this.differenceClassBorders = borders;
    },
    async fetchAdresses(projId: number, compId: number, offId: number) {
      return new Promise((resolve, reject) => {
        JmApi.get(Adress.getUrlBuilder(projId, compId, offId))
          .then((data) => {
            // @ts-ignore
            this.adresses = data;
            resolve("success");
          })
          .catch((data) => {
            reject(data.message);
          });
      });
    },
    async fetchMetricsFirst(
      projId: number,
      compId: number,
      offId: number,
      transport?: string | null,
      metricType?: string | null,
    ) {
      // console.log("fetchMetricsFirst called with transport: ", transport);
      try {
        if (transport) {
          this.firstTransport = transport;
          // console.log(
          //   "firstTransport in fetchMetricsFirst: ",
          //   this.firstTransport,
          // );
        }

        if (!this.firstTransport) {
          throw new Error(
            "Could not fetch metrics, because first transport not given",
          );
        }

        const data = await MetricsManager.get(
          projId,
          compId,
          offId,
          this.firstTransport,
          metricType || undefined,
          usePlzSurchargeStore().enabled,
        );

        // console.log("fetchMetricsFirst received data:", data);
        this.metricsFirst = data;
        return "success";
      } catch (error) {
        console.error("fetchMetricsFirst error:", error);
        throw error;
      }
    },
    async fetchMetricsSecond(
      projId: number,
      compId: number,
      offId: number,
      transport?: string,
      metricType?: string,
    ) {
      try {
        if (transport) {
          this.secondTransport = transport;
          // console.log(
          //   "secondTransport in fetchMetricsSecond: ",
          //   this.secondTransport,
          // );
        }

        if (!this.secondTransport) {
          console.warn(
            "Could not fetch metrics, because second transport not given",
          );
          return;
        }

        const data = await MetricsManager.get(
          projId,
          compId,
          offId,
          this.secondTransport,
          metricType || undefined,
          usePlzSurchargeStore().enabled,
        );

        // console.log("fetchMetricsSecond received data:", data);
        this.metricsSecond = data;
        return "success";
      } catch (error) {
        console.error("fetchMetricsSecond error:", error);
        throw error;
      }
    },
    /**
     * Fetch parameters for a specific transport type using office-specific parameters.
     * Falls back to default parameters if no office is set.
     * @param transport - Transport type (e.g., 'car', 'train', 'bike')
     * @returns Parameter object for the transport type
     */
    async fetchParametersByTransport(transport: string) {
      const cleverRouteStore = useCleverRouteImportStore();

      // Get current office from the central office store
      const officeStore = useOfficeStore();
      const officeId = officeStore.selectedOffice?.office_id;

      if (!officeId) {
        console.warn(
          "No office context available for parameter fetching, using defaults",
        );
        // Fallback: fetch default parameters if no office context
        const response = await JmApi.get(
          `${PARAMETERS_BY_TRANSPORT_URL}?transport_type=${transport}`,
        );
        return response;
      }

      // Ensure office parameters are loaded
      if (
        cleverRouteStore.currentOfficeId !== officeId ||
        cleverRouteStore.officeParameters.length === 0
      ) {
        await cleverRouteStore.fetchOfficeParameters(officeId);
      }

      // Get parameter for specific transport type
      const parameter = cleverRouteStore.getParameterByTransport(transport);

      if (!parameter) {
        console.warn(
          `No parameter found for transport type: ${transport}, office: ${officeId}`,
        );
        return null;
      }

      return parameter;
    },
    async fetchRelativeData(
      firstAddressId: number,
      lastAddressId: number,
      officeId: number,
    ) {
      const cleverRouteImportStore = useCleverRouteImportStore();

      // Fetch CR import results
      const crImportResults = await cleverRouteImportStore.fetchCrImportResults(
        firstAddressId,
        lastAddressId,
        officeId,
      );

      // console.log("crImportResults: ", crImportResults);

      if (!crImportResults) {
        console.warn("No CR import results found");
        return;
      }

      // Process the data as needed
      this.relativeData = crImportResults.reduce((acc, result) => {
        acc[result.MA_ID] = result;
        return acc;
      }, {});

      return this.relativeData;
    },
    setOffice(office: Office) {
      this.office = office;
    },
    async fetchZipCodeData() {
      // console.log("fetchZipCodeData called");
      if (!this.adresses || this.adresses.length === 0) {
        console.warn("No employee addresses available to fetch zip code data");
        return;
      }

      const uniqueZipCodes = [
        ...new Set(this.adresses.map((addr) => addr.postal_code)),
      ];
      try {
        this.zipCodeData = await ZipCodes.getZipCodeData(uniqueZipCodes);
        // console.log("zipCodeData in mapInputStore: ", this.zipCodeData);
      } catch (error) {
        console.error("Error fetching zip code data:", error);
      }
    },

    /**
     * Update best alternative filters
     * @param filters New filter configuration
     */
    updateBestAlternativeFilters(filters: Partial<BestAlternativeFilters>) {
      this.bestAlternativeFilters = {
        ...this.bestAlternativeFilters,
        ...filters,
      };
    },

    /**
     * Reset best alternative filters to defaults
     */
    resetBestAlternativeFilters() {
      this.bestAlternativeFilters = { ...DEFAULT_BEST_ALTERNATIVE_FILTERS };
    },

    /**
     * Fetch leg count for specific path and transport combination
     * Uses the backend endpoint created in Phase 1
     * @param pathId Path identifier
     * @param routeTransport Transport mode
     * @returns Number of legs (transport changes)
     */
    async fetchLegCount(
      pathId: number,
      routeTransport: string,
    ): Promise<number> {
      try {
        return await TransportService.getLegCount(pathId, routeTransport);
      } catch (error) {
        console.error(
          `Error fetching leg count for path ${pathId}, transport ${routeTransport}:`,
          error,
        );
        return 999; // Return high number to filter out problematic routes
      }
    },

    /**
     * Batch fetch leg counts for multiple path/transport combinations
     * Optimizes multiple API calls
     * @param requests Array of {pathId, routeTransport} objects
     * @returns Map of "pathId-transport" -> legCount
     */
    async fetchMultipleLegCounts(
      requests: { pathId: number; routeTransport: string }[],
    ): Promise<Map<string, number>> {
      const results = new Map<string, number>();

      // Process requests in parallel for better performance
      const promises = requests.map(async (req) => {
        const key = `${req.pathId}-${req.routeTransport}`;
        const legCount = await this.fetchLegCount(
          req.pathId,
          req.routeTransport,
        );
        results.set(key, legCount);
      });

      await Promise.all(promises);
      return results;
    },
  },
});
